import { useState, useEffect } from 'react';
import * as theme from '../../../theme';
import dayjs from 'dayjs';
import TruCard from '../../TruCard';
import ScanStatusItem from '../items/ScanStatusItem';
import { doc, getDoc, runTransaction } from 'firebase/firestore';
import { useNavigate } from 'react-router';
import { db } from '../../../firebase';
import ConfirmationModal from '../../ConfirmationModal';
import ScanPaymentModal from '../../ScanPaymentModal';
import { Button, Box, Typography, CircularProgress } from '@mui/material';
import CostContext from '../../../contexts/CostContext';
import BillingCheckModal from '../../BillingCheckModal';
import { handleScanAction } from '../../../functions/scanStatusUtils';
import TrackingModal from '../../TrackingModal';
import ExtraImprintPaymentModal from '../../ExtraImprintPaymentModal';
import { getSecondImprint } from '../../../contexts/FocusScanContext';
import getServerTimestamp from '../../../functions/endpoints/GetServerTimestamp';

export default function ScanStatusList({ scan, clientId, scanId, currentUser, setScan, setError }) {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [trackingAction, setTrackingAction] = useState(null);
  const [processingActions, setProcessingActions] = useState({});
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [scanPaymentModalOpen, setScanPaymentModalOpen] = useState(false);
  const [extraImprintModalOpen, setExtraImprintModalOpen] = useState(false);
  const [cardId, setCardId] = useState(null);
  const [cardBrand, setCardBrand] = useState('');
  const [cardLast4, setCardLast4] = useState('');
  const [practitionerId, setPractitionerId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFollowUp, setIsFollowUp] = useState(false);
  const [isImmediateFamily, setIsImmediateFamily] = useState(false);
  const [needsExtraImprint, setNeedsExtraImprint] = useState(false);

  useEffect(() => {
    const fetchPractitionerIdAndCard = async () => {
      try {
        setIsLoading(true);
        const clientRef = doc(db, 'clients', clientId);
        const clientSnap = await getDoc(clientRef);
        if (clientSnap.exists()) {
          setPractitionerId(clientSnap.data().practitionerId);
          setIsImmediateFamily(clientSnap.data().immediateFamily);
          const clientScans = clientSnap.data().scans || [];
          const prevComplete = clientScans.filter(s => s.id !== scanId && s.isComplete === true).length > 0;
          setIsFollowUp(prevComplete);
        } else {
          console.error('Client not found for ID:', clientId);
          setError('Client not found.');
          navigate(`/clients/${clientId}`);
          return;
        }

        if (!scan || Object.keys(scan).length === 0) {
          console.warn('Scan data not loaded yet for scanId:', scanId);
          return;
        }

        if (scan.reports && scan.reports.focusScans && scan.reports.focusScans.length > 0) {
          const hasExtra = scan.reports.focusScans.some(f => getSecondImprint(f.focusScanType));
          setNeedsExtraImprint(hasExtra);
        }

        if (scan.cardId && scan.isFreeScan !== true) {
          const billingRef = doc(db, 'billing', clientSnap.data().practitionerId);
          const billingSnap = await getDoc(billingRef);
          if (billingSnap.exists()) {
            const cards = billingSnap.data().cards || [];
            const selectedCard = cards.find(card => card.cardId === scan.cardId);
            if (selectedCard) {
              setCardId(selectedCard.cardId);
              setCardBrand(selectedCard.brand);
              setCardLast4(selectedCard.last4);
            } else {
              console.warn(`Card with ID ${scan.cardId} not found or disabled for practitioner ${clientSnap.data().practitionerId}`);
              setError('Selected card not found or disabled. Please select a new card.');
              setBillingModalOpen(true);
            }
          } else {
            console.warn(`Billing document not found for practitioner ${clientSnap.data().practitionerId}`);
            setError('Billing document not found. Please add a card.');
            setBillingModalOpen(true);
          }
        } else if (scan.isFreeScan !== true) {
          setBillingModalOpen(true);
        }
      } catch (err) {
        console.error('Error fetching client or card details:', err);
        setError('Error fetching client or card details: ' + err.message);
        navigate(`/clients/${clientId}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPractitionerIdAndCard();
  }, [clientId, navigate, setError, scan, scanId]);

  const handleAction = (action) => {
    if (action === 'kitMailed' || action === 'imprintMailed') {
      setTrackingAction(action);
      setTrackingModalOpen(true);
    } else if (action === 'scanPaid') {
      if (scan.isFreeScan === true || cardId) {
        setScanPaymentModalOpen(true);
      } else {
        setBillingModalOpen(true);
      }
    } else {
      setCurrentAction(action);
      setModalOpen(true);
    }
  };

  const handleScanPaymentConfirm = async () => {
    setScanPaymentModalOpen(false);
    await handleActionExecution('scanPaid');
  };

  const handleExtraCharge = () => {
    if (scan.isFreeScan === true || cardId) {
      setExtraImprintModalOpen(true);
    } else {
      setBillingModalOpen(true);
    }
  };

  const handleExtraImprintConfirm = async () => {
    setExtraImprintModalOpen(false);
    await handleActionExecution('extraImprintCharged');
  };

  const handleActionExecution = async (action, trackings = null) => {
    setProcessingActions((prev) => ({ ...prev, [action]: true }));

    try {
      const updatedScan = await handleScanAction({
        action,
        clientId,
        scanId,
        scan,
        currentUser,
        cardId,
        practitionerId,
        trackings,
      });

      setScan(updatedScan);
    } catch (err) {
      console.error(`Error executing action ${action}:`, err);
      let errorMessage = err.message;
      if (err.message.includes('NO_CARD_ON_FILE')) {
        errorMessage = 'Cannot process payment, we do not have a card on file to charge.';
      } else if (err.message.includes('CARD_DECLINED')) {
        errorMessage = 'Cannot process payment, card declined due to insufficient funds or other issue.';
      } else if (err.message.includes('SERVICE_UNAVAILABLE')) {
        errorMessage = 'Payment system is currently unavailable. Please try again later.';
      } else if (err.message.includes('timed out')) {
        errorMessage = action === 'cancel' ? 'Cancellation processing timed out. Please try again.' : 'Processing timed out. Please try again.';
      } else if (err.message.includes('Client document not found')) {
        errorMessage = 'Failed to update client scan status: Client not found.';
      } else if (err.message.includes('Schedule document not found')) {
        errorMessage = 'Failed to update schedule: Schedule not found.';
      } else if (err.message.includes('Refund failed')) {
        errorMessage = err.message;
      } else if (err.message.includes('missing practitioner record')) {
        errorMessage = 'Failed to process action: Practitioner not assigned to client.';
      } else if (err.message.includes('Refund function is not available')) {
        errorMessage = 'Cancellation failed: Refund functionality is not available. Please contact support.';
      } else if (err.message.includes('Original transaction not found')) {
        errorMessage = 'Failed to process refund: Original transaction not found.';
      } else if (err.message.includes('Payment ID not found')) {
        errorMessage = 'Failed to process refund: Payment ID not found for transaction.';
      } else if (err.message.includes('INVALID_CARD')) {
        errorMessage = 'The selected card is invalid or disabled. Please select a new card.';
      } else if (err.message.includes('CARD_EXPIRED')) {
        errorMessage = 'The selected card has expired. Please select a new card.';
      }
      setError(errorMessage);
      if (err.message.includes('NO_CARD_ON_FILE') || err.message.includes('INVALID_CARD') || err.message.includes('CARD_EXPIRED')) {
        setBillingModalOpen(true);
      }
    } finally {
      setProcessingActions((prev) => ({ ...prev, [action]: false }));
    }
  };

  const handleTrackingConfirm = async (trackings) => {
    setTrackingModalOpen(false);
    await handleActionExecution(trackingAction, trackings);
  };

  const handleCardValidated = async (billingInfo) => {
    setCardId(billingInfo.cardId);
    setCardBrand(billingInfo.brand);
    setCardLast4(billingInfo.last4);

    try {
      const scanRef = doc(db, 'clients', clientId, 'scans', scanId);
      await runTransaction(db, async (transaction) => {
        const scanSnap = await getDoc(scanRef);
        if (!scanSnap.exists()) {
          throw new Error('Scan document not found.');
        }
        transaction.update(scanRef, {
          cardId: billingInfo.cardId,
          dateModified: await getServerTimestamp(),
        });
      });
      setScan({ ...scan, cardId: billingInfo.cardId });
    } catch (err) {
      console.error('Error updating scan with new card:', err);
      setError('Failed to update scan with new card: ' + err.message);
    }

    setBillingModalOpen(false);
  };

  const getModalConfig = () => {
    switch (currentAction) {
      case 'kitMailed':
        return {
          title: 'Confirm Kit Mailed',
          description: 'Confirm that the kit has been mailed to the client.',
          confirmButtonText: 'Confirm',
        };
      case 'reportsUploaded':
        return {
          title: 'Confirm Reports Uploaded',
          description: 'Confirm that all reports have been uploaded.',
          confirmButtonText: 'Confirm',
        };
      case 'imprintMailed':
        return {
          title: 'Confirm Imprint Mailed',
          description: 'Confirm that the imprint has been mailed to the client.',
          confirmButtonText: 'Confirm',
        };
      case 'cancel':
        return {
          title: 'Confirm Scan Cancellation',
          description: scan.kitMailed
            ? `Canceling this scan will not refund the $${CostContext.ScanDown} downpayment, as the kit has been mailed. This action cannot be undone. Are you sure you want to cancel?`
            : `Canceling this scan will refund the $${CostContext.ScanDown} downpayment. This action cannot be undone. Are you sure you want to cancel?`,
          confirmButtonText: 'Confirm Cancellation',
        };
      default:
        return {};
    }
  };

  const handleConfirm = async () => {
    await handleActionExecution(currentAction);
    setModalOpen(false);
  };

  const clientFormReady = scan.clientForm && scan.clientForm.dateCreated
    ? { actionLink: () => navigate(`/clients/${clientId}/scans/${scanId}/viewform`) }
    : {};

  const showExtraButton = needsExtraImprint && !scan.chargedExtraImprint && scan.scanPaid && !scan.cancelled;

  if (isLoading || !scan || Object.keys(scan).length === 0) {
    return (
      <TruCard title={'Status'}>
        <Typography>Loading...</Typography>
      </TruCard>
    );
  }

  return (
    <TruCard
      title={'Status'}
      styles={{
        marginTop:
          (scan.scanPaid && currentUser.role !== 'practitioner') || scan.reportsUploaded ? 20 : 0,
      }}
    >
      {scan.isFreeScan === true && (
        <Typography variant="body1" sx={{ color: theme.Colors.Red, textAlign: 'center', marginBottom: 2, fontWeight: 600 }}>
          Complimentary Practitioner Scan
        </Typography>
      )}
      {scan.multiHomeDiscount === true && (
        <Typography variant="body1" sx={{ color: theme.Colors.Red, textAlign: 'center', marginBottom: 2, fontWeight: 600 }}>
          Multi-Scan Discount
        </Typography>
      )}
      <ScanStatusItem
        action="kitPaid"
        icon="money"
        label="Downpayment Paid"
        disabled={!scan.kitPaid}
        content={scan.kitPaid && scan.kitPaid.datePaid ? dayjs(scan.kitPaid.datePaid.toDate()).format('MM/DD/YYYY h:mm A') : null}
      />
      <ScanStatusItem
        action="kitMailed"
        icon="mail"
        label="Kit Mailed"
        disabled={!scan.kitMailed}
        nextUp={!scan.kitMailed && scan.kitPaid && scan.kitPaid.datePaid && !scan.cancelled}
        content={scan.kitMailed && scan.kitMailed.dateMailed ? dayjs(scan.kitMailed.dateMailed.toDate()).format('MM/DD/YYYY h:mm A') : null}
        onComplete={() => handleAction('kitMailed')}
        canComplete={currentUser.role === 'manager' || currentUser.role === 'admin'}
        processing={processingActions['kitMailed']}
      />
      <ScanStatusItem
        action="clientForm"
        icon="report"
        label="Client Form"
        disabled={!scan.clientForm}
        nextUp={!scan.clientForm && scan.kitMailed && scan.kitPaid.dateMailed && !scan.cancelled}
        content={scan.clientForm && scan.clientForm.dateCreated ? dayjs(scan.clientForm.dateCreated.toDate()).format('MM/DD/YYYY h:mm A') : null}
        {...clientFormReady}
      />
      <ScanStatusItem
        action="scanPaid"
        icon="money"
        label="Scan Paid in Full"
        disabled={!scan.scanPaid}
        nextUp={!scan.scanPaid && scan.clientForm && scan.clientForm.dateCreated && !scan.cancelled}
        content={
          processingActions['scanPaid']
            ? 'Processing...'
            : scan.scanPaid && scan.scanPaid.datePaid
            ? dayjs(scan.scanPaid.datePaid.toDate()).format('MM/DD/YYYY h:mm A')
            : null
        }
        onComplete={() => handleAction('scanPaid')}
        canComplete={currentUser.role === 'manager' || currentUser.role === 'admin'}
        processing={processingActions['scanPaid']}
      />
      <ScanStatusItem
        action="reportsUploaded"
        icon="report"
        label="Reports Uploaded"
        disabled={!scan.reportsUploaded}
        nextUp={!scan.reportsUploaded && scan.scanPaid && scan.scanPaid.datePaid && !scan.cancelled}
        content={
          scan.reportsUploaded && scan.reportsUploaded.dateUploaded
            ? dayjs(scan.reportsUploaded.dateUploaded.toDate()).format('MM/DD/YYYY h:mm A')
            : null
        }
        onComplete={() => handleAction('reportsUploaded')}
        canComplete={currentUser.role === 'manager' || currentUser.role === 'admin'}
        processing={processingActions['reportsUploaded']}
      />
      <ScanStatusItem
        action="imprintMailed"
        icon="mail"
        label="Imprint Mailed"
        disabled={!scan.imprintMailed}
        nextUp={!scan.imprintMailed && scan.scanPaid && scan.scanPaid.datePaid && !scan.cancelled}
        content={
          scan.imprintMailed && scan.imprintMailed.dateMailed
            ? dayjs(scan.imprintMailed.dateMailed.toDate()).format('MM/DD/YYYY h:mm A')
            : null
        }
        onComplete={() => handleAction('imprintMailed')}
        canComplete={currentUser.role === 'manager' || currentUser.role === 'admin'}
        processing={processingActions['imprintMailed']}
      />
      {(processingActions['cancel'] || scan.cancelled) && (
        <ScanStatusItem
          action="cancelled"
          icon="cancelled"
          label="Scan Cancelled"
          disabled={!scan.cancelled}
          nextUp={!scan.cancelled}
          content={
            processingActions['cancel']
              ? 'Processing...'
              : scan.cancelled && scan.cancelled.dateCancelled
              ? dayjs(scan.cancelled.dateCancelled.toDate()).format('MM/DD/YYYY h:mm A')
              : null
          }
          processing={processingActions['cancel']}
        />
      )}
      {!scan.scanPaid && !scan.cancelled && scan.isFreeScan !== true && (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => handleAction('cancel')}
            disabled={processingActions['cancel']}
            startIcon={processingActions['cancel'] ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {processingActions['cancel'] ? 'Cancelling...' : 'Cancel Scan'}
          </Button>
        </Box>
      )}
      {showExtraButton && currentUser.role !== 'practitioner' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExtraCharge}
            disabled={processingActions['extraImprintCharged']}
            startIcon={processingActions['extraImprintCharged'] ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {processingActions['extraImprintCharged'] ? 'Charging...' : 'Charge Extra Imprint'}
          </Button>
        </Box>
      )}
      <ConfirmationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirm}
        {...getModalConfig()}
      />
      <ScanPaymentModal
        open={scanPaymentModalOpen}
        onClose={() => setScanPaymentModalOpen(false)}
        onConfirm={handleScanPaymentConfirm}
        cardBrand={cardBrand}
        cardLast4={cardLast4}
        isFreeScan={scan.isFreeScan === true}
        isFollowUp={isFollowUp}
        isMultiple={scan.multiHomeDiscount === true}
        isPractitionerFamily={isImmediateFamily}
        practitionerId={practitionerId}
        outgoingTracking={scan.kitMailed?.outgoingUSPS || ''}
        scanId={scanId}
      />
      <TrackingModal
        open={trackingModalOpen}
        action={trackingAction}
        onClose={() => setTrackingModalOpen(false)}
        onConfirm={handleTrackingConfirm}
      />
      <BillingCheckModal
        practitionerId={practitionerId}
        open={billingModalOpen}
        onClose={() => setBillingModalOpen(false)}
        onCardValidated={handleCardValidated}
      />
      <ExtraImprintPaymentModal
        open={extraImprintModalOpen}
        onClose={() => setExtraImprintModalOpen(false)}
        onConfirm={handleExtraImprintConfirm}
        cardBrand={cardBrand}
        cardLast4={cardLast4}
      />
    </TruCard>
  );
}